// Bottom-right strip showing every in-flight + recently-completed
// upload. Backed by `UploadsProvider` — components don't render
// progress themselves, they queue an upload and the toast appears.

import { CheckCircle2, FileUp, X } from "lucide-react";

import { cn } from "@link/ui";

import { useUploads } from "../state/Uploads";

export function UploadToasts() {
  const { entries, dismiss } = useUploads();
  if (entries.length === 0) return null;
  return (
    <div className="pointer-events-none fixed bottom-[calc(4.75rem+env(safe-area-inset-bottom))] right-3 z-[180] flex w-[min(20rem,calc(100vw-1.5rem))] flex-col gap-2 lg:bottom-4 lg:right-4">
      {entries.map((e) => (
        <div
          key={e.id}
          className={cn(
            "pointer-events-auto overflow-hidden rounded-xl border bg-white shadow-lg",
            e.status === "error"
              ? "border-red-200"
              : e.status === "done"
                ? "border-emerald-200"
                : "border-cloud-200",
          )}
        >
          <div className="flex items-start gap-3 p-3">
            <div
              className={cn(
                "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg",
                e.status === "error"
                  ? "bg-red-50 text-red-600"
                  : e.status === "done"
                    ? "bg-emerald-50 text-emerald-600"
                    : "bg-strawberry-50 text-strawberry-600",
              )}
            >
              {e.status === "done" ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <FileUp className="h-4 w-4" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-cloud-800">
                {e.filename}
              </p>
              <p className="text-[11px] text-cloud-500">
                {e.status === "error"
                  ? (e.errorMessage ?? "Upload failed")
                  : e.status === "done"
                    ? "Uploaded"
                    : `Uploading · ${formatBytes(e.byteSize)}`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => dismiss(e.id)}
              className="flex h-6 w-6 items-center justify-center rounded-md text-cloud-400 hover:bg-cloud-100 hover:text-cloud-700"
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {e.status === "uploading" && (
            <div className="h-1 w-full bg-cloud-100">
              <div
                className="h-full bg-strawberry-gradient transition-all"
                style={{ width: `${Math.round(e.progress * 100)}%` }}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(1)} GB`;
}
